import React, { useEffect, useRef, useState } from 'react';
import cometdLib from 'cometd';

import { CometdActions } from './cometD/cometD-action';
import { CometDProvider } from './cometD/cometdProvider';
import { fetchConfigDetailsFromClientHost, loadLocalUser, watchForChatSDKEvents } from "./Simplify360Chat/Services/HWIframeBridge";
import { s360ConfigService } from "./Simplify360Chat/Services/S360Config";
import HWUser from "./Simplify360Chat/HWLibrary/HWUser";
import Simplify360Chat from "./Simplify360Chat/Simplify360Chat";

import "./App.css";

function App() {
  const [cometdInstance] = useState(new cometdLib.CometD());
  const appRef = useRef();
  const [cometUser, setCometUser] = useState('');
  const [cometToken, setCometToken] = useState('');
  const [user, setUser] = useState(null);
  const [chatMaximized, setChatMaximized] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(true);
  const [isShowLanguageDropdown, setIsShowLanguageDropdown] = useState(true);

  useEffect(() => {
    const storedLanguage = (sessionStorage.getItem("selectedLanguage"));
    const storedDropdownStatus = (sessionStorage.getItem("showLanguageDropdown"));
    if (storedLanguage != null) {
      setSelectedLanguage(storedLanguage);
    }
    if (storedDropdownStatus != null) {
      setIsShowLanguageDropdown(false);
    }
  }, []);

  useEffect(() => {
    localStorage.clear();
    loadLocalUser();
    s360ConfigService.loadClientConfigFromToken();
    sessionStorage.setItem("handshaken", "false");
    watchForChatSDKEvents({ setUser, setChatMaximized, setRefresh }, () => { });
    fetchConfigDetailsFromClientHost();
    
    // Setup simple CometD logging listeners
    cometdInstance.addListener('/meta/handshake', (message) => {
      console.log('COMETD-DEBUG: /meta/handshake', {
        successful: message.successful,
        error: message.error,
        clientId: message.clientId
      });
    });
    
    cometdInstance.addListener('/meta/connect', (message) => {
      console.log('COMETD-DEBUG: /meta/connect', { successful: message.successful });
    });
    
    cometdInstance.addListener('/meta/disconnect', (message) => {
      console.log('COMETD-DEBUG: /meta/disconnect', { successful: message.successful });
      sessionStorage.setItem("handshaken", "false");
      localStorage.removeItem("subscribed");
    });
    
    cometdInstance.addListener('/meta/unsuccessful', (message) => {
      console.error('COMETD-DEBUG: /meta/unsuccessful', {
        channel: message.channel,
        error: message.error
      });
    });
  }, []);

  const configureCometd = async () => {
    const channel = localStorage.getItem("clientChannel");
    
    // Get credentials: prefer React state, fallback to HWUser (for page refresh)
    let finalCometUser = cometUser || HWUser.cometUser();
    let finalCometToken = cometToken || HWUser.cometToken();
    
    console.log('COMETD-DEBUG: configureCometd called', {
      channel,
      fromState: { user: cometUser, token: cometToken ? '***' : null },
      fromHWUser: { user: HWUser.cometUser(), token: HWUser.cometToken() ? '***' : null },
      final: { user: finalCometUser, token: finalCometToken ? '***' : null }
    });
    
    // Validate credentials
    if (!finalCometUser || !finalCometToken || finalCometUser === 'undefined' || finalCometToken === 'undefined') {
      console.error('COMETD-DEBUG: Missing credentials! Cannot establish connection.');
      return Promise.reject('COMETD-LOG: Missing authentication credentials');
    }
    
    let additional = {
      ext: {
        "com.inrev.authn": {
          user: finalCometUser,
          token: finalCometToken
        }
      },
    };

    const host = s360ConfigService.config.host;
    let connectionURL = `https://${host}/bmcometd/cometd`;
    
    console.log('COMETD-DEBUG: Connection URL', connectionURL);

    cometdInstance.websocketEnabled = true;
    cometdInstance.configure({
      url: connectionURL.trim(),
      maxNetworkDelay: 30000, // Increased from 10s to 30s for long-polling
      backoffIncrement: 1000,
      maxBackoff: 60000,
      requestHeaders: {
        'Connection': 'keep-alive' // Help maintain long-polling connections
      }
    });

    return new Promise((resolve, reject) => {
      if (sessionStorage.getItem("handshaken") !== "true") {
        console.log('COMETD-DEBUG: Initiating handshake...');
        
        cometdInstance.handshake(additional, function (handshakeReply) {
          console.log('COMETD-DEBUG: Handshake reply', {
            successful: handshakeReply.successful,
            error: handshakeReply.error,
            clientId: handshakeReply.clientId
          });
          
          if (handshakeReply.successful) {
            sessionStorage.setItem("handshaken", "true");
            console.log("COMETD-LOG: HANDSHAKE SUCCESSFUL", { clientId: handshakeReply.clientId });
            
            const { subscribe } = CometdActions(cometdInstance);
            subscribe(channel, appRef);
            resolve("COMETD-LOG:HANDSHAKE SUCCESSFUL");
          } else {
            console.error("COMETD-LOG: HANDSHAKE FAILED", handshakeReply.error);
            reject(`COMETD-LOG:HANDSHAKE FAILED - ${handshakeReply.error || 'Unknown error'}`);
          }
        });
      } else {
        console.log('COMETD-DEBUG: Already handshaken, subscribing to channel', { channel });
        const { subscribe } = CometdActions(cometdInstance);
        subscribe(channel, appRef);
        resolve("COMETD-LOG:HANDSHAKE SUCCESSFUL");
      }
    });
  };

  return (
    <CometDProvider cometdInstance={cometdInstance}>
      <div ref={appRef} id="App">
        {s360ConfigService.isConfigured() && (
          <Simplify360Chat
            user={user}
            chatMaximized={chatMaximized}
            refresh={refresh}
            configureCometd={configureCometd}
            appRef={appRef}
            isShowLanguageDropdown={isShowLanguageDropdown}
            setIsShowLanguageDropdown={setIsShowLanguageDropdown}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            setCometUser={setCometUser}
            setCometToken={setCometToken}
          />
        )}
      </div>
    </CometDProvider>
  );
}

export default App;
